import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import {
  ApiError,
  type BrandTemplateResponse,
  deleteBrandTemplateMutation,
  downloadBrandTemplate,
  getBrandQueryKey,
  getBrandTemplateThumbnail,
  getBrandTemplateThumbnailQueryKey,
  listBrandTemplatesQueryKey,
  uploadBrandTemplateMutation,
} from "@/api";

export type TemplateFormat = BrandTemplateResponse["format"];

export const TEMPLATE_FORMATS: readonly TemplateFormat[] = ["pptx", "docx", "xlsx"];

/** Extensions the backend accepts per slot (a normal file or a real template). */
export const TEMPLATE_EXTENSIONS: Record<TemplateFormat, readonly string[]> = {
  pptx: [".pptx", ".potx"],
  docx: [".docx", ".dotx"],
  xlsx: [".xlsx", ".xltx"],
};

/** Mirrors the backend's limit. */
export const TEMPLATE_MAX_BYTES = 25 * 1024 * 1024;

/** Backend rejection codes with a translated message. */
export const TEMPLATE_ERROR_CODES = [
  "TEMPLATE_FORMAT_UNSUPPORTED",
  "TEMPLATE_EMPTY",
  "TEMPLATE_TOO_LARGE",
  "TEMPLATE_MACROS",
  "TEMPLATE_NOT_OFFICE",
  "TEMPLATE_INVALID",
  "TEMPLATE_ENCRYPTED",
  "TEMPLATE_ACTIVE_CONTENT",
] as const;
export type TemplateErrorCode = (typeof TEMPLATE_ERROR_CODES)[number];

export type TemplateProblem = "type" | "size";

/** Client-side check before uploading: the slot's extensions and the size limit. */
export function checkTemplate(file: File, format: TemplateFormat): TemplateProblem | null {
  const name = file.name.toLowerCase();
  if (!TEMPLATE_EXTENSIONS[format].some((ext) => name.endsWith(ext))) return "type";
  if (file.size > TEMPLATE_MAX_BYTES) return "size";
  return null;
}

/** The backend's rejection code, when it's one we translate. */
export function templateErrorCode(error: unknown): TemplateErrorCode | null {
  const code = error instanceof ApiError ? error.code : null;
  return TEMPLATE_ERROR_CODES.includes(code as TemplateErrorCode) ? (code as TemplateErrorCode) : null;
}

const path = (brandUuid: string, format: TemplateFormat) => ({ brand_uuid: brandUuid, template_format: format });

/** The thumbnail is an authenticated image GET, so it's fetched as a blob and shown via an object URL. */
export function useTemplateThumbnailUrl(brandUuid: string, template: BrandTemplateResponse): string | undefined {
  const thumbnail = useQuery({
    // updatedAt in the key: a replaced template gets a fresh thumbnail.
    queryKey: [...getBrandTemplateThumbnailQueryKey({ path: path(brandUuid, template.format) }), template.updatedAt],
    queryFn: async ({ signal }) => {
      const { data } = await getBrandTemplateThumbnail({
        path: path(brandUuid, template.format),
        parseAs: "blob",
        signal,
        throwOnError: true,
      });
      return data as Blob;
    },
    enabled: Boolean(template.hasThumbnail),
    retry: false,
  });
  const url = useMemo(() => (thumbnail.data ? URL.createObjectURL(thumbnail.data) : undefined), [thumbnail.data]);
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);
  return url;
}

function useInvalidateTemplates(brandUuid: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getBrandQueryKey({ path: { brand_uuid: brandUuid } }) }),
      queryClient.invalidateQueries({ queryKey: listBrandTemplatesQueryKey({ path: { brand_uuid: brandUuid } }) }),
    ]);
}

export function useUploadTemplate(brandUuid: string) {
  const invalidate = useInvalidateTemplates(brandUuid);
  const upload = useMutation({ ...uploadBrandTemplateMutation(), onSuccess: invalidate });
  return {
    isPending: upload.isPending,
    upload: (format: TemplateFormat, file: File) =>
      upload.mutateAsync({ path: path(brandUuid, format), body: { file }, throwOnError: true }),
  };
}

export function useRemoveTemplate(brandUuid: string) {
  const invalidate = useInvalidateTemplates(brandUuid);
  const remove = useMutation({ ...deleteBrandTemplateMutation(), onSuccess: invalidate });
  return {
    isPending: remove.isPending,
    remove: (format: TemplateFormat) => remove.mutateAsync({ path: path(brandUuid, format), throwOnError: true }),
  };
}

/** Fetch the stored file through the authenticated client and save it under its original name. */
export async function downloadTemplate(brandUuid: string, template: BrandTemplateResponse): Promise<void> {
  const { data } = await downloadBrandTemplate({
    path: path(brandUuid, template.format),
    parseAs: "blob",
    throwOnError: true,
  });
  const url = URL.createObjectURL(data as Blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = template.filename || `template.${template.format}`;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
